/*
Resource names that are used in metering or limits.
See backend references:
- packages/roboto_internal/src/roboto_internal/metering/metering_model.py
- packages/roboto_internal/src/roboto_internal/limiting/limiting_model.py
*/
export enum Resource {
  // AI
  AiCreditMillionths = "ai::credit_millionths",

  // Datasets
  DatasetsCreated = "datasets::created",
  DatasetCount = "datasets::total_count",

  // Actions
  ActionCount = "actions::total_count",
  ActionTimeout = "actions::timeout_minutes",
  ConcurrentInvocations = "actions::concurrent_invocations",
  FreeInvocationComputeCreditMillionths = "actions::free_invocation_compute_credit_millionths",
  FreeTierComputeSpec = "actions::free_tier_compute_spec",
  InvocationComputeCreditMillionths = "actions::invocation_compute_credit_millionths",

  /*
  Raw compute consumption, in each substrate's own units, under two generations
  of name: the `Retired*` names (`actions::…`) and the substrate-qualified names
  that replaced them (`actions::fargate::…`, `actions::microvm::…`). One
  quantity, such as invocations scheduled or runtime, is spread across several
  members, so a surface that shows it totals one of the families in
  @/shared/domain/metering/meteredResourceFamilies.ts.

  Nothing writes the `Retired*` names any more, but reports still contain them:
  a `metering_reports.values` snapshot is written once per closed month and
  never rewritten.

  `VCpuMillis` and `RamGBMillis` are legacy misnomers. They store CPU units
  (1024 per vCPU) × seconds and MiB × seconds.

  Kept in step with `MeteredResourceName` in
  packages/roboto_internal/src/roboto_internal/metering/metering_model.py.
  */
  RetiredInvocationsScheduled = "actions::invocations_scheduled",
  RetiredInvocationVCpuMillis = "actions::invocation_vcpu_millis",
  RetiredInvocationRamGBMillis = "actions::invocation_ram_gb_millis",
  RetiredInvocationRuntimeMillis = "actions::invocation_runtime_millis",

  FargateInvocationsScheduled = "actions::fargate::invocations_scheduled",
  FargateInvocationVCpuMillis = "actions::fargate::invocation_vcpu_millis",
  FargateInvocationRamGBMillis = "actions::fargate::invocation_ram_gb_millis",
  FargateInvocationRuntimeMillis = "actions::fargate::invocation_runtime_millis",
  /** What the platform is billed for Fargate compute, at AWS list rates, no margin. */
  FargateProviderCostMicrodollars = "actions::fargate::provider_cost_microdollars",

  MicrovmInvocationsScheduled = "actions::microvm::invocations_scheduled",
  MicrovmInvocationVCpuMillis = "actions::microvm::invocation_vcpu_millis",
  MicrovmInvocationRamGBMillis = "actions::microvm::invocation_ram_gb_millis",
  MicrovmInvocationRuntimeMillis = "actions::microvm::invocation_runtime_millis",
  /** What the platform is billed for MicroVM vCPU and memory time and each VM start, at AWS list rates, no margin. */
  MicrovmProviderCostMicrodollars = "actions::microvm::provider_cost_microdollars",

  MemoryMiBs = "actions::memory_mibs_per_invocation",
  VCPUs = "actions::vcpus_per_invocation",
  StorageGiBs = "actions::storage_gibs_per_invocation",
  GPUs = "actions::gpus_per_invocation",

  // Files
  FreeManagedDataDownloadBytes = "files::free_managed_data_download_bytes",
  FreeManagedDataStoredBytes = "files::free_managed_data_stored_bytes",
  ManagedDataInternalDownloadBytes = "files::managed_data_internal_download_bytes",
  ManagedDataDownloadBytes = "files::managed_data_download_bytes",
  ManagedDataStoredBytes = "files::managed_data_stored_bytes",
  ManagedDataStoredFiles = "files::managed_data_stored_files",
  ManagedDataInternalUploadBytes = "files::managed_data_internal_upload_bytes",
  ManagedDataUploadBytes = "files::managed_data_upload_bytes",

  // Custom fields
  CustomFieldsPerEntityType = "custom_fields::definitions_per_entity_type",

  // Orgs
  OrgMembers = "orgs::total_members",
  OrgServiceUsers = "orgs::total_service_users",

  // Users
  OwnedOrgsPerUser = "users::total_owned_orgs",

  // Triggers
  TriggersActive = "triggers::active_count",
}
